import React, { useEffect, useState } from 'react';
import {
  Box, Tab, Tabs, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip, Typography
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { getPremiumTransferRequests, decidePremiumTransfer, searchCustomerByPolicyNumber, getPolicyByGuid } from '../../lib/requests.es6';
import { hasAuthourity } from '../../lib/access.es6';
import { approvalErrorMessage, transferNeedsFollowUp } from '../../lib/premiumApprovals';

var TABS = [
  { label: 'Pending',  status: 'Pending' },
  { label: 'Approved', status: 'Approved' },
  { label: 'Rejected', status: 'Rejected' },
  { label: 'Returned', status: 'Returned' },
  { label: 'Executed', status: 'Executed' },
];

var STATUS_COLOR = {
  Pending:  'warning',
  Approved: 'info',
  Rejected: 'error',
  Returned: 'warning',
  Executed: 'success',
};

function shortGuid(guid) {
  if (!guid) return '—';
  return guid.slice(0, 8) + '…';
}

function fmtAmount(cents, currency) {
  if (typeof cents !== 'number') return '—';
  var major = (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (currency || 'KES') + ' ' + major;
}

function fmtDate(str) {
  if (!str) return '—';
  try { return new Date(str).toLocaleString(); } catch (e) { return str; }
}

function DetailRow(props) {
  return (
    <Box sx={{ display: 'flex', py: 0.75, borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' } }}>
      <Typography sx={{ width: 150, flexShrink: 0, color: 'text.secondary', fontSize: '12px' }}>
        {props.label}
      </Typography>
      <Typography sx={{ fontWeight: 500, fontSize: '12px', wordBreak: 'break-all' }}>
        {props.value || '—'}
      </Typography>
    </Box>
  );
}

function PolicyCard(props) {
  var policyNumber = props.policyNumber;
  var policyGuid = props.policyGuid;

  var loaded = useState({ data: null, fetching: false });
  var state = loaded[0];
  var setState = loaded[1];

  useEffect(function() {
    var identifier = policyNumber || policyGuid;
    if (!identifier) return;
    setState({ data: null, fetching: true });
    var request = policyNumber
      ? searchCustomerByPolicyNumber({ policy_number: policyNumber })
      : getPolicyByGuid(policyGuid);
    request
      .then(function(res) {
        var r = (res && res.data) || {};
        setState({
          fetching: false,
          data: {
            policy_number: r.policy_number || policyNumber || '',
            status: r.status || '',
            product_name: r.product_name || '',
            start_date: r.start_date || '',
          }
        });
      })
      .catch(function() {
        setState({ data: null, fetching: false });
      });
  }, [policyNumber, policyGuid]);

  var d = state.data;

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 2, mb: 1 }}>
      <Typography sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary', fontSize: '11px', display: 'block', mb: 1, fontWeight: 600 }}>
        {props.label}
      </Typography>
      {state.fetching ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={14} />
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Loading…</Typography>
        </Box>
      ) : (
        <Box>
          <DetailRow label="Policy number" value={d && d.policy_number || policyNumber} />
          <DetailRow label="Product" value={d && d.product_name} />
          <DetailRow
            label="Status"
            value={d && d.status
              ? React.createElement(Chip, { label: d.status, color: STATUS_COLOR[d.status] || 'default', size: 'small' })
              : null}
          />
          <DetailRow label="Start date" value={d && d.start_date && d.start_date !== 'null' ? d.start_date : null} />
          {typeof props.amount === 'number' && (
            <DetailRow label="Transfer amount" value={fmtAmount(props.amount, 'KES')} />
          )}
          {props.newReference && (
            <DetailRow label="New reference" value={React.createElement('span', { style: { fontFamily: 'monospace', fontSize: '11px' } }, props.newReference)} />
          )}
        </Box>
      )}
    </Box>
  );
}

function TransferDetailModal(props) {
  var transfer = props.transfer;
  var onClose = props.onClose;
  var onDecided = props.onDecided;

  var s1 = useState(false);
  var rejectMode = s1[0];
  var setRejectMode = s1[1];

  var s2 = useState('');
  var rejectReason = s2[0];
  var setRejectReason = s2[1];

  var s3 = useState('');
  var rejectError = s3[0];
  var setRejectError = s3[1];

  var s4 = useState(false);
  var submitting = s4[0];
  var setSubmitting = s4[1];

  var s5 = useState('');
  var submitError = s5[0];
  var setSubmitError = s5[1];

  var isPending = transfer.status === 'Pending' && hasAuthourity('APPROVE_PREMIUM_TRANSFER');
  var needsFollowUp = transferNeedsFollowUp(transfer);
  var destinations = Array.isArray(transfer.destinations) ? transfer.destinations : [];

  function handleApprove() {
    setSubmitting(true);
    setSubmitError('');
    decidePremiumTransfer(transfer.guid, 'Approved', null)
      .then(function() {
        onDecided('Transfer approved and executed successfully.');
      })
      .catch(function(e) {
        var msg = approvalErrorMessage(e, 'Approval failed.');
        setSubmitError(msg);
        setSubmitting(false);
      });
  }

  function handleRejectConfirm() {
    if (!rejectReason.trim()) {
      setRejectError('Rejection reason is required.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    setRejectError('');
    decidePremiumTransfer(transfer.guid, 'Rejected', rejectReason.trim())
      .then(function() {
        onDecided('Transfer request rejected.');
      })
      .catch(function(e) {
        var msg = approvalErrorMessage(e, 'Rejection failed.');
        setRejectError(msg);
        setSubmitting(false);
      });
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwapHorizIcon sx={{ color: '#FBA92D', fontSize: 20 }} />
        Transfer Request Details
      </DialogTitle>

      <DialogContent dividers>
        {submitError && (
          <p style={{ color: 'red', marginBottom: '12px', fontSize: '13px' }}>{submitError}</p>
        )}

        {needsFollowUp && (
          <p style={{ color: '#9b1c1c', marginBottom: '12px', fontSize: '13px', fontWeight: 600 }}>
            This transfer needs finishing: money has moved but the transfer was
            not completed. A person must finish it.
          </p>
        )}

        {/* Transfer summary */}
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 2, mb: 2 }}>
          <Typography sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary', fontSize: '11px', display: 'block', mb: 1, fontWeight: 600 }}>
            Transfer
          </Typography>
          <DetailRow label="Reference" value={transfer.transfer_reference || shortGuid(transfer.guid)} />
          <DetailRow label="Amount" value={fmtAmount(transfer.transfer_amount_in_cents, 'KES')} />
          <DetailRow label="Reason" value={transfer.reason} />
          <DetailRow label="Initiated by" value={transfer.initiated_by_name || transfer.initiated_by} />
          <DetailRow label="Date" value={fmtDate(transfer.created_at)} />
          <DetailRow
            label="Status"
            value={React.createElement(Chip, { label: needsFollowUp ? 'Approved: needs finishing' : transfer.status, color: needsFollowUp ? 'error' : (STATUS_COLOR[transfer.status] || 'default'), size: 'small' })}
          />
          {transfer.rejection_reason && <DetailRow label="Rejection reason" value={transfer.rejection_reason} />}
          {transfer.approved_by_name && <DetailRow label="Decided by" value={transfer.approved_by_name} />}
        </Box>

        {/* Source policy */}
        <PolicyCard
          label="Source policy (premium removed)"
          policyNumber={transfer.source_policy_number}
          policyGuid={transfer.source_policy_guid}
        />

        {/* Destination policies */}
        {destinations.map(function(d, i) {
          return React.createElement(PolicyCard, {
            key: i,
            label: destinations.length > 1 ? 'Destination policy ' + (i + 1) : 'Destination policy',
            policyNumber: d.policy_number,
            policyGuid: d.policy_guid,
            amount: d.amount_in_cents,
            newReference: d.new_reference,
          });
        })}

        {/* Rejection reason input */}
        {rejectMode && (
          <Box sx={{ mt: 2 }}>
            {rejectError && <p style={{ color: 'red', marginBottom: '8px', fontSize: '13px' }}>{rejectError}</p>}
            <label style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>
              Rejection reason (required)
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={rejectReason}
              onChange={function(e) { setRejectReason(e.target.value); }}
              placeholder="Explain why this transfer is being rejected…"
              disabled={submitting}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {rejectMode ? (
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={function() { setRejectMode(false); setRejectError(''); setRejectReason(''); }}
              disabled={submitting}
              style={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleRejectConfirm}
              disabled={submitting}
              style={{ backgroundColor: '#c53030', color: '#fff', textTransform: 'none' }}
            >
              {submitting ? 'Rejecting…' : 'Confirm rejection'}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={onClose} disabled={submitting} style={{ textTransform: 'none' }}>
              Close
            </Button>
            {isPending && (
              <Button
                variant="outlined"
                onClick={function() { setRejectMode(true); setSubmitError(''); }}
                disabled={submitting}
                style={{ textTransform: 'none', color: '#c53030', borderColor: '#c53030' }}
              >
                Reject
              </Button>
            )}
            {isPending && (
              <Button
                variant="contained"
                onClick={handleApprove}
                disabled={submitting}
                style={{ backgroundColor: '#4B7E34', color: '#fff', textTransform: 'none' }}
              >
                {submitting ? 'Approving…' : 'Approve'}
              </Button>
            )}
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function PremiumTransferDashboard() {
  var s1 = useState(0);
  var tabIndex = s1[0];
  var setTabIndex = s1[1];

  var s2 = useState([]);
  var transfers = s2[0];
  var setTransfers = s2[1];

  var s3 = useState(false);
  var loading = s3[0];
  var setLoading = s3[1];

  var s4 = useState('');
  var pageError = s4[0];
  var setPageError = s4[1];

  var s5 = useState('');
  var actionMsg = s5[0];
  var setActionMsg = s5[1];

  var s6 = useState(null);
  var viewTarget = s6[0];
  var setViewTarget = s6[1];

  var currentStatus = TABS[tabIndex].status;

  useEffect(function() {
    load(currentStatus);
  }, [tabIndex]);

  function load(status) {
    setLoading(true);
    setPageError('');
    setActionMsg('');
    getPremiumTransferRequests(null, status)
      .then(function(res) {
        setTransfers((res && res.data) || []);
      })
      .catch(function() {
        setPageError('Failed to load transfer requests.');
        setTransfers([]);
      })
      .finally(function() {
        setLoading(false);
      });
  }

  function handleDecided(msg) {
    setViewTarget(null);
    setActionMsg(msg);
    load(currentStatus);
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SwapHorizIcon sx={{ color: '#FBA92D' }} />
        <h5 style={{ margin: 0 }}>Premium Transfer Approvals</h5>
      </Box>

      {actionMsg && <p style={{ color: 'green', marginBottom: '12px' }}>{actionMsg}</p>}
      {pageError && <p style={{ color: 'red', marginBottom: '12px' }}>{pageError}</p>}

      <Tabs
        value={tabIndex}
        onChange={function(_, v) { setTabIndex(v); }}
        sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}
      >
        {TABS.map(function(t, i) {
          return React.createElement(Tab, { key: t.status, label: t.label, value: i });
        })}
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : transfers.length === 0 ? (
        <p style={{ textAlign: 'center', marginTop: '40px', opacity: 0.6 }}>
          No {currentStatus.toLowerCase()} transfer requests.
        </p>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                <TableCell><strong>Reference</strong></TableCell>
                <TableCell><strong>Source Policy</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Initiated By</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transfers.map(function(t) {
                return (
                  <TableRow key={t.guid} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {t.transfer_reference || shortGuid(t.guid)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px' }}>
                      {t.source_policy_number || shortGuid(t.source_policy_guid)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px' }}>{fmtAmount(t.transfer_amount_in_cents, 'KES')}</TableCell>
                    <TableCell sx={{ fontSize: '12px' }}>{t.initiated_by_name || t.initiated_by || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{fmtDate(t.created_at)}</TableCell>
                    <TableCell>
                      <Chip label={transferNeedsFollowUp(t) ? 'Approved: needs finishing' : t.status} color={transferNeedsFollowUp(t) ? 'error' : (STATUS_COLOR[t.status] || 'default')} size="small" />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={function() { setViewTarget(t); }}
                        style={{ fontSize: '11px', textTransform: 'none' }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {viewTarget && (
        <TransferDetailModal
          transfer={viewTarget}
          onClose={function() { setViewTarget(null); }}
          onDecided={handleDecided}
        />
      )}
    </Box>
  );
}
